import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  proyectosApi,
  municipiosApi,
  oficinasApi,
  getApiError,
} from "../api/catalogosApi";


const CATALOGOS = [
  {
    id: "PROYECTOS",
    label: "Proyectos",
    singular: "Proyecto",
    description:
      "Catálogo general de proyectos disponibles en el sistema.",
  },
  {
    id: "MUNICIPIOS",
    label: "Municipios",
    singular: "Municipio",
    description:
      "Catálogo general de municipios disponibles para asignación.",
  },
  {
    id: "OFICINAS",
    label: "Oficinas",
    singular: "Oficina",
    description:
      "Oficinas disponibles, organizadas por municipio.",
  },
];


const EMPTY_FORM = {
  nombre: "",
  descripcion: "",
  municipioId: "",
};


function cn(...values) {
  return values
    .filter(Boolean)
    .join(" ");
}


function formatDate(value) {
  if (!value) {
    return "—";
  }

  try {
    return new Date(
      value
    ).toLocaleString(
      "es-MX",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  } catch {
    return "—";
  }
}


export default function Configuracion() {
  const [
    categoria,
    setCategoria
  ] = useState("PROYECTOS");

  const [
    items,
    setItems
  ] = useState([]);

  const [
    municipios,
    setMunicipios
  ] = useState([]);

  const [
    form,
    setForm
  ] = useState(EMPTY_FORM);

  const [
    editingId,
    setEditingId
  ] = useState(null);

  const [
    panelOpen,
    setPanelOpen
  ] = useState(false);

  const [
    buscar,
    setBuscar
  ] = useState("");

  const [
    includeInactive,
    setIncludeInactive
  ] = useState(false);

  const [
    filtroMunicipio,
    setFiltroMunicipio
  ] = useState("");

  const [
    loading,
    setLoading
  ] = useState(false);

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    mensaje,
    setMensaje
  ] = useState("");

  const [
    error,
    setError
  ] = useState("");


  const catalogoActual =
    CATALOGOS.find(
      item =>
        item.id === categoria
    ) || CATALOGOS[0];


  const limpiarMensajes = () => {
    setMensaje("");
    setError("");
  };


  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };


  const cerrarPanel = () => {
    resetForm();
    setPanelOpen(false);
    limpiarMensajes();
  };


  // ============================================================
  // MUNICIPIOS BASE PARA OFICINAS
  // ============================================================

  const cargarMunicipiosBase =
    useCallback(async () => {

      const data =
        await municipiosApi.listar({
          includeInactive: true,
        });

      setMunicipios(
        Array.isArray(data)
          ? data
          : []
      );

      return data;
    }, []);


  useEffect(() => {
    cargarMunicipiosBase()
      .catch((err) => {
        console.error(err);
        setError(
          getApiError(err)
        );
      });
  }, [
    cargarMunicipiosBase,
  ]);


  // ============================================================
  // CARGAR CATÁLOGO PRINCIPAL
  // ============================================================

  const cargarItems =
    useCallback(async () => {

      try {
        setLoading(true);
        setError("");

        let data = [];

        if (
          categoria === "PROYECTOS"
        ) {
          data =
            await proyectosApi.listar(
              includeInactive
            );
        }

        if (
          categoria === "MUNICIPIOS"
        ) {
          data =
            await municipiosApi.listar({
              includeInactive,
            });
        }

        if (
          categoria === "OFICINAS"
        ) {
          data =
            await oficinasApi.listar({
              includeInactive,
              municipioId:
                filtroMunicipio || null,
            });
        }

        setItems(
          Array.isArray(data)
            ? data
            : []
        );

      } catch (err) {
        console.error(err);

        setError(
          getApiError(err)
        );
      } finally {
        setLoading(false);
      }

    }, [
      categoria,
      includeInactive,
      filtroMunicipio,
    ]);


  useEffect(() => {
    cargarItems();
  }, [
    cargarItems,
  ]);


  // ============================================================
  // CAMBIO DE CATÁLOGO
  // ============================================================

  const cambiarCategoria =
    (id) => {

      setCategoria(id);

      setBuscar("");
      setIncludeInactive(false);
      setFiltroMunicipio("");

      resetForm();
      setPanelOpen(false);
      limpiarMensajes();
    };


  // ============================================================
  // MUNICIPIOS ACTIVOS
  // ============================================================

  const municipiosActivos =
    useMemo(
      () =>
        municipios.filter(
          item =>
            item.activo !== false
        ),
      [municipios]
    );


  // ============================================================
  // BÚSQUEDA LOCAL
  // ============================================================

  const itemsFiltrados =
    useMemo(() => {

      const term =
        buscar
          .trim()
          .toLowerCase();

      if (!term) {
        return items;
      }

      return items.filter(
        item => {

          const text = [
            item.nombre,
            item.descripcion,
            item.municipioNombre,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return text.includes(
            term
          );
        }
      );

    }, [
      items,
      buscar,
    ]);


  // ============================================================
  // FORM
  // ============================================================

  const abrirNuevo = () => {
    resetForm();
    limpiarMensajes();
    setPanelOpen(true);
  };


  const editar = (item) => {
    limpiarMensajes();

    setEditingId(
      item.id
    );

    setForm({
      nombre:
        item.nombre || "",

      descripcion:
        item.descripcion || "",

      municipioId:
        item.municipioId || "",
    });

    setPanelOpen(true);
  };


  const changeForm =
    (field, value) => {

      setForm(
        current => ({
          ...current,
          [field]:
            value,
        })
      );
    };


  const validar = () => {

    if (
      categoria === "OFICINAS" &&
      !form.municipioId
    ) {
      return "Selecciona un municipio.";
    }

    if (
      !form.nombre.trim()
    ) {
      return "Captura el nombre.";
    }

    return null;
  };


  // ============================================================
  // GUARDAR
  // ============================================================

  const [habilitarInsumosNuevaOficina, setHabilitarInsumosNuevaOficina] = useState(false);

  const guardar =
    async () => {

      limpiarMensajes();

      const validation =
        validar();

      if (validation) {
        setError(validation);
        return;
      }

      try {
        setSaving(true);

        const payload = {
          nombre:
            form.nombre.trim(),

          descripcion:
            form.descripcion.trim(),
        };


        if (
          categoria === "OFICINAS"
        ) {
          payload.municipioId =
            form.municipioId;
        }


        if (
          categoria === "PROYECTOS"
        ) {

          if (editingId) {
            await proyectosApi.actualizar(
              editingId,
              payload
            );
          } else {
            await proyectosApi.crear(
              payload
            );
          }
        }


        if (
          categoria === "MUNICIPIOS"
        ) {

          if (editingId) {
            await municipiosApi.actualizar(
              editingId,
              payload
            );
          } else {
            await municipiosApi.crear(
              payload
            );
          }
        }


        if (
          categoria === "OFICINAS"
        ) {

          if (editingId) {
            await oficinasApi.actualizar(
              editingId,
              payload
            );
          } else {
            await oficinasApi.crear(
                { ...payload, ...(habilitarInsumosNuevaOficina ? { modulosHabilitados: ["INSUMOS_LICENCIAS"] } : {}) }
              );
          }
        }


        setMensaje(
          editingId
            ? `${catalogoActual.singular} actualizado correctamente.`
            : `${catalogoActual.singular} creado correctamente.`
        );

        resetForm();
        setPanelOpen(false);

        await Promise.all([
          cargarItems(),
          cargarMunicipiosBase(),
        ]);

      } catch (err) {
        console.error(err);

        setError(
          getApiError(err)
        );
      } finally {
        setSaving(false);
      }
    };


  // ============================================================
  // DESACTIVAR
  // ============================================================

  const desactivar =
    async (item) => {

      if (
        item.activo === false
      ) {
        return;
      }

      const confirmado =
        window.confirm(
          `¿Deseas desactivar "${item.nombre}"?`
        );

      if (!confirmado) {
        return;
      }

      limpiarMensajes();

      try {

        if (
          categoria === "PROYECTOS"
        ) {
          await proyectosApi.desactivar(
            item.id
          );
        }

        if (
          categoria === "MUNICIPIOS"
        ) {
          await municipiosApi.desactivar(
            item.id
          );
        }

        if (
          categoria === "OFICINAS"
        ) {
          await oficinasApi.desactivar(
            item.id
          );
        }

        setMensaje(
          `${catalogoActual.singular} desactivado correctamente.`
        );

        await Promise.all([
          cargarItems(),
          cargarMunicipiosBase(),
        ]);

      } catch (err) {
        console.error(err);

        setError(
          getApiError(err)
        );
      }
    };


  return (
    <div className="min-h-screen bg-[#f6f7f9]">

      <div className="mx-auto max-w-[1600px] px-5 py-7 lg:px-8">

        {/* HEADER */}

        <header className="mb-6 flex flex-col gap-4 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">

          <div>

            <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
              Administración
            </p>

            <h1 className="text-[28px] font-bold tracking-tight text-slate-950">
              Catálogos del inventario
            </h1>

            <p className="mt-1 max-w-2xl text-sm text-slate-500">
              Administra los catálogos maestros utilizados por los módulos del sistema.
            </p>

          </div>


          <div className="flex items-center gap-2">

            <button
              onClick={
                cargarItems
              }
              disabled={
                loading
              }
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >

              <span
                className={cn(
                  "text-base",
                  loading &&
                    "animate-spin"
                )}
              >
                ↻
              </span>

              Refrescar

            </button>


            <button
              onClick={
                abrirNuevo
              }
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800"
            >

              <span className="text-lg leading-none">
                +
              </span>

              Nuevo{" "}
              {catalogoActual.singular.toLowerCase()}

            </button>

          </div>

        </header>


        {/* TABS */}

        <div className="mb-5 border-b border-slate-200">

          <nav className="-mb-px flex gap-7">

            {CATALOGOS.map(
              cat => {

                const active =
                  cat.id === categoria;

                return (
                  <button
                    key={cat.id}
                    onClick={() =>
                      cambiarCategoria(
                        cat.id
                      )
                    }
                    className={cn(
                      "relative pb-3 text-sm font-semibold transition",
                      active
                        ? "text-slate-950"
                        : "text-slate-500 hover:text-slate-800"
                    )}
                  >

                    {cat.label}

                    {active && (
                      <span className="absolute inset-x-0 bottom-0 h-0.5 bg-slate-950" />
                    )}

                  </button>
                );
              }
            )}

          </nav>

        </div>


        {/* CONTEXTO */}

        <div className="mb-4">

          <div className="mb-4">

            <h2 className="text-lg font-bold text-slate-900">
              {catalogoActual.label}
            </h2>

            <p className="mt-0.5 text-sm text-slate-500">
              {catalogoActual.description}
            </p>

          </div>


          {/* TOOLBAR */}

          <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.03)] lg:flex-row lg:items-center">

            <div className="relative min-w-0 flex-1">

              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                ⌕
              </span>

              <input
                type="text"
                value={
                  buscar
                }
                onChange={
                  e =>
                    setBuscar(
                      e.target.value
                    )
                }
                placeholder={`Buscar ${catalogoActual.label.toLowerCase()}...`}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
              />

            </div>


            {categoria === "OFICINAS" && (

              <select
                value={
                  filtroMunicipio
                }
                onChange={
                  e =>
                    setFiltroMunicipio(
                      e.target.value
                    )
                }
                className="h-10 min-w-[250px] rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-slate-400"
              >

                <option value="">
                  Todos los municipios
                </option>

                {municipios.map(
                  municipio => (

                    <option
                      key={
                        municipio.id
                      }
                      value={
                        municipio.id
                      }
                    >
                      {municipio.nombre}
                    </option>

                  )
                )}

              </select>

            )}


            <label className="flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3">

              <input
                type="checkbox"
                checked={
                  includeInactive
                }
                onChange={
                  e =>
                    setIncludeInactive(
                      e.target.checked
                    )
                }
                className="h-4 w-4 rounded border-slate-300"
              />

              <span className="text-xs font-semibold text-slate-600">
                Mostrar inactivos
              </span>

            </label>

          </div>

        </div>


        {/* ALERTAS */}

        {mensaje && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">

            <span>
              {mensaje}
            </span>

            <button
              onClick={() =>
                setMensaje("")
              }
              className="font-bold"
            >
              ×
            </button>

          </div>
        )}


        {error && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">

            <span>
              {error}
            </span>

            <button
              onClick={() =>
                setError("")
              }
              className="font-bold"
            >
              ×
            </button>

          </div>
        )}


        {/* MAIN */}

        <div
          className={cn(
            "grid items-start gap-5 transition-all",
            panelOpen
              ? "grid-cols-1 xl:grid-cols-[minmax(0,1fr)_390px]"
              : "grid-cols-1"
          )}
        >


          {/* TABLA */}

          <section className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.03)]">

            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">

              <p className="text-xs font-semibold text-slate-500">

                {loading
                  ? "Consultando..."
                  : `${itemsFiltrados.length} registro${
                      itemsFiltrados.length === 1
                        ? ""
                        : "s"
                    }`
                }

              </p>


              {(buscar ||
                filtroMunicipio) && (

                <button
                  onClick={() => {
                    setBuscar("");
                    setFiltroMunicipio("");
                  }}
                  className="text-xs font-semibold text-slate-500 hover:text-slate-900"
                >
                  Limpiar filtros
                </button>
              )}

            </div>


            {loading ? (

              <div className="flex min-h-[420px] items-center justify-center">

                <div className="flex flex-col items-center gap-3 text-sm text-slate-400">

                  <div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-200 border-t-slate-800" />

                  Consultando catálogo...

                </div>

              </div>

            ) : itemsFiltrados.length === 0 ? (

              <div className="flex min-h-[420px] items-center justify-center px-6">

                <div className="max-w-sm text-center">

                  <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-lg text-slate-700">
                    +
                  </div>

                  <h3 className="text-sm font-bold text-slate-900">
                    No hay registros para mostrar
                  </h3>

                  <p className="mt-1 text-sm text-slate-500">
                    Crea un nuevo registro o modifica los filtros de búsqueda.
                  </p>

                  <button
                    onClick={
                      abrirNuevo
                    }
                    className="mt-4 text-sm font-semibold text-slate-900 underline underline-offset-4"
                  >
                    Crear{" "}
                    {catalogoActual.singular.toLowerCase()}
                  </button>

                </div>

              </div>

            ) : (

              <div className="overflow-x-auto">

                <table className="min-w-full">

                  <thead>

                    <tr className="border-b border-slate-200 bg-[#fafafa]">

                      <TableHead>
                        Nombre
                      </TableHead>

                      {categoria === "OFICINAS" && (
                        <TableHead>
                          Municipio
                        </TableHead>
                      )}

                      <TableHead>
                        Estado
                      </TableHead>

                      <TableHead className="hidden xl:table-cell">
                        Última actualización
                      </TableHead>

                      <TableHead align="right">
                        Acciones
                      </TableHead>

                    </tr>

                  </thead>


                  <tbody className="divide-y divide-slate-100">

                    {itemsFiltrados.map(
                      item => (

                        <tr
                          key={
                            item.id
                          }
                          className="group transition hover:bg-slate-50"
                        >

                          <td className="px-5 py-4">

                            <div className="max-w-[480px]">

                              <p className="text-sm font-semibold text-slate-900">
                                {item.nombre}
                              </p>

                              {item.descripcion && (

                                <p className="mt-1 truncate text-xs text-slate-500">
                                  {item.descripcion}
                                </p>

                              )}

                            </div>

                          </td>


                          {categoria === "OFICINAS" && (

                            <td className="px-5 py-4">

                              <p className="text-sm text-slate-700">
                                {item.municipioNombre || "Sin municipio"}
                              </p>

                            </td>

                          )}


                          <td className="px-5 py-4">

                            <StatusBadge
                              active={
                                item.activo !== false
                              }
                            />

                          </td>


                          <td className="hidden px-5 py-4 text-xs text-slate-500 xl:table-cell">

                            {formatDate(
                              item.updatedAt
                            )}

                          </td>


                          <td className="px-5 py-4">

                            <div className="flex items-center justify-end gap-1">

                              <button
                                onClick={() =>
                                  editar(
                                    item
                                  )
                                }
                                className="rounded-md px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-950"
                              >
                                Editar
                              </button>


                              {item.activo !== false && (

                                <button
                                  onClick={() =>
                                    desactivar(
                                      item
                                    )
                                  }
                                  className="rounded-md px-2.5 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                                >
                                  Desactivar
                                </button>

                              )}

                            </div>

                          </td>

                        </tr>

                      )
                    )}

                  </tbody>

                </table>

              </div>

            )}

          </section>


          {/* PANEL */}

          {panelOpen && (

            <aside className="sticky top-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.06)]">

              <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">

                <div>

                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                    {editingId
                      ? "Editar registro"
                      : "Nuevo registro"}
                  </p>

                  <h3 className="mt-1 text-base font-bold text-slate-950">

                    {editingId
                      ? `Editar ${catalogoActual.singular.toLowerCase()}`
                      : `Nuevo ${catalogoActual.singular.toLowerCase()}`
                    }

                  </h3>

                </div>


                <button
                  onClick={
                    cerrarPanel
                  }
                  className="flex h-8 w-8 items-center justify-center rounded-md text-xl leading-none text-slate-400 transition hover:bg-slate-100 hover:text-slate-800"
                >
                  ×
                </button>

              </div>


              <div className="space-y-5 p-5">


                {categoria === "OFICINAS" && (

                  <FormField
                    label="Municipio"
                    required
                  >

                    <select
                      value={
                        form.municipioId
                      }
                      onChange={
                        e =>
                          changeForm(
                            "municipioId",
                            e.target.value
                          )
                      }
                      className="input-enterprise"
                    >

                      <option value="">
                        Selecciona un municipio
                      </option>

                      {municipiosActivos.map(
                        municipio => (

                          <option
                            key={
                              municipio.id
                            }
                            value={
                              municipio.id
                            }
                          >
                            {municipio.nombre}
                          </option>

                        )
                      )}

                    </select>

                  </FormField>

                )}


                <FormField
                  label="Nombre"
                  required
                >

                  <input
                    type="text"
                    autoFocus
                    value={
                      form.nombre
                    }
                    onChange={
                      e =>
                        changeForm(
                          "nombre",
                          e.target.value
                        )
                    }
                    placeholder={
                      categoria === "PROYECTOS"
                        ? "Ej. LICENCIAS"
                        : categoria === "MUNICIPIOS"
                        ? "Ej. Benito Juárez"
                        : "Ej. Dirección de Tránsito"
                    }
                    className="input-enterprise"
                  />

                </FormField>


                <FormField
                  label="Descripción"
                  helper="Opcional"
                >

                  <textarea
                    rows={5}
                    value={
                      form.descripcion
                    }
                    onChange={
                      e =>
                        changeForm(
                          "descripcion",
                          e.target.value
                        )
                    }
                    placeholder="Descripción administrativa del registro..."
                    className="input-enterprise resize-none"
                  />

                </FormField>


                {categoria === "OFICINAS" && (

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">

                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Municipio asignado
                    </p>

                    <p className="mt-1 text-xs font-semibold text-slate-700">

                      {
                        municipios.find(
                          item =>
                            item.id ===
                            form.municipioId
                        )?.nombre ||
                        "No seleccionado"
                      }

                    </p>

                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      El mismo nombre de oficina puede existir en municipios distintos.
                    </p>

                  </div>

                )}

              </div>


              <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">

                <button
                  onClick={
                    cerrarPanel
                  }
                  disabled={
                    saving
                  }
                  className="h-9 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancelar
                </button>


                {categoria === "OFICINAS" && !editingId && (
  <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
    <input
      type="checkbox"
      checked={habilitarInsumosNuevaOficina}
      onChange={(event) => setHabilitarInsumosNuevaOficina(event.target.checked)}
      className="h-4 w-4 accent-slate-900"
    />
    Habilitar esta oficina para Insumos de Licencias
  </label>
)}

<button
  onClick={guardar}
                  disabled={
                    saving
                  }
                  className="h-9 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
                >

                  {saving
                    ? "Guardando..."
                    : editingId
                    ? "Guardar cambios"
                    : "Crear registro"}

                </button>

              </div>

            </aside>

          )}

        </div>


        <style>{`
          .input-enterprise {
            width: 100%;
            min-height: 42px;
            border-radius: 8px;
            border: 1px solid rgb(203 213 225);
            background: white;
            padding: 9px 11px;
            font-size: 14px;
            color: rgb(15 23 42);
            outline: none;
            transition:
              border-color 120ms ease,
              box-shadow 120ms ease;
          }

          .input-enterprise::placeholder {
            color: rgb(148 163 184);
          }

          .input-enterprise:hover:not(:disabled) {
            border-color: rgb(148 163 184);
          }

          .input-enterprise:focus {
            border-color: rgb(71 85 105);
            box-shadow:
              0 0 0 3px rgba(148,163,184,0.16);
          }

          .input-enterprise:disabled {
            cursor: not-allowed;
            background: rgb(248 250 252);
          }
        `}</style>

      </div>

    </div>
  );
}


function TableHead({
  children,
  align = "left",
  className = "",
}) {
  return (
    <th
      className={cn(
        "px-5 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500",
        align === "right"
          ? "text-right"
          : "text-left",
        className
      )}
    >
      {children}
    </th>
  );
}


function StatusBadge({
  active,
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
        active
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-500"
      )}
    >

      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          active
            ? "bg-emerald-500"
            : "bg-slate-400"
        )}
      />

      {active
        ? "Activo"
        : "Inactivo"}

    </span>
  );
}


function FormField({
  label,
  helper,
  required,
  children,
}) {
  return (
    <div>

      <div className="mb-1.5 flex items-center justify-between">

        <label className="text-xs font-semibold text-slate-700">

          {label}

          {required && (
            <span className="ml-1 text-red-500">
              *
            </span>
          )}

        </label>


        {helper && (
          <span className="text-[11px] text-slate-400">
            {helper}
          </span>
        )}

      </div>

      {children}

    </div>
  );
}